import { html, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import EkoElement from "@/utilities/element";
import { formatFileSize } from "./types";
import style from "./upload-item.css?inline";
import "@/components/avatar";
import "@/components/icon";
import "@/components/progress-bar";
import "./file-type-icon";

@customElement("eko-upload-item")
class UploadItem extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  @property({ reflect: true })
  error?: string;

  @property({ reflect: true })
  fileName: string = "";

  @property({ reflect: true, type: Number })
  fileSize: number = 0;

  @property({ reflect: true, attribute: "file-type" })
  fileType: string = "";

  @property({ reflect: true, attribute: "file-url" })
  fileUrl: string = "";

  @property({ reflect: true, type: Number })
  progress: number = 0;

  @property({ reflect: true, type: Boolean })
  removable: boolean = true;

  @property({ reflect: true })
  status: "pending" | "uploading" | "success" | "error" | "existing" = "pending";

  private getFileExtension(): string {
    const parts = this.fileName.split(".");
    if (parts.length > 1) {
      return parts[parts.length - 1];
    }
    return "";
  }

  private getStatusIcon() {
    switch (this.status) {
      case "success":
        return "check-circle";
      case "error":
        return "x-circle";
      case "uploading":
        return "upload-cloud-02";
      case "existing":
      case "pending":
      default:
        return undefined;
    }
  }

  private getStatusText() {
    switch (this.status) {
      case "success":
        return "Complete";
      case "error":
        return this.error || "Error";
      case "uploading":
        return "Uploading...";
      case "existing":
      case "pending":
      default:
        return "";
    }
  }

  private handleRemove() {
    this.dispatchEvent(
      new CustomEvent("remove", {
        detail: { fileName: this.fileName },
        bubbles: true,
        composed: true,
      })
    );
  }

  private isImageWithUrl(): boolean {
    return Boolean(this.fileUrl && this.fileType.includes("image"));
  }

  render() {
    const formattedSize = formatFileSize(this.fileSize);
    const uploadedSize = formatFileSize(Math.floor((this.fileSize * this.progress) / 100));
    const statusIcon = this.getStatusIcon();
    const statusText = this.getStatusText();

    return html`
      <div class="eko-upload-item" part="root" data-status=${this.status}>
        <div class="eko-upload-item__content">
          ${this.isImageWithUrl()
            ? html`<eko-avatar
                src=${this.fileUrl}
                alt=${this.fileName}
                size="md"
                part="icon"></eko-avatar>`
            : html`<eko-file-type-icon
                extension=${this.getFileExtension()}
                part="icon"></eko-file-type-icon>`}
          <div class="eko-upload-item__info" part="info">
            <div class="eko-upload-item__text">
              <p class="eko-upload-item__name" part="name">${this.fileName}</p>
              <div class="eko-upload-item__meta">
                <span class="eko-upload-item__size" part="size">
                  ${this.status === "uploading"
                    ? `${uploadedSize} of ${formattedSize}`
                    : formattedSize}
                </span>
                ${statusText
                  ? html`
                      <span class="eko-upload-item__divider"></span>
                      <div class="eko-upload-item__status" part="status">
                        ${statusIcon
                          ? html`<eko-icon name=${statusIcon} size="md"></eko-icon>`
                          : ""}
                        <span>${statusText}</span>
                      </div>
                    `
                  : ""}
              </div>
            </div>
            ${this.status === "uploading" || this.status === "success"
              ? html`
                  <div class="eko-upload-item__progress" part="progress">
                    <eko-progress-bar value=${this.progress} max="100"></eko-progress-bar>
                    <span class="eko-upload-item__percentage">${Math.round(this.progress)}%</span>
                  </div>
                `
              : ""}
          </div>
        </div>
        ${this.removable
          ? html`
              <button
                class="eko-upload-item__remove"
                part="remove"
                @click=${this.handleRemove}
                aria-label="Remove ${this.fileName}"
                type="button">
                <eko-icon name="trash-01" size="md"></eko-icon>
              </button>
            `
          : ""}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-upload-item": UploadItem;
  }
}

export default UploadItem;
